import {
  WarningIcon,
  BroadcastIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseCircleIcon,
  EnvelopeSimpleIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { RadarPing } from '@/components/motion/RadarPing';

type AlertStatus =
  | 'pending'
  | 'action-required'
  | 'counter-review'
  | 'live'
  | 'paused'
  | 'finished'
  | 'declined';

interface StatusConfig {
  title: string;
  description: string;
  icon: PhosphorIcon;
  chip: string;
  glow: string | null;
  pulse?: string;
}

const NEUTRAL_CHIP = 'bg-muted text-foreground ring-border';

const STATUS_CONFIG: Record<AlertStatus, StatusConfig> = {
  pending: {
    title: 'Offer Received',
    description: 'Review the details and accept, decline, or counter.',
    icon: EnvelopeSimpleIcon,
    chip: 'bg-amber/10 text-amber ring-amber/25',
    glow: 'bg-amber',
  },
  'action-required': {
    title: 'Action Required',
    description:
      'The Goloot team has sent a counter offer. Review the updated terms and respond.',
    icon: WarningIcon,
    chip: 'bg-amber/10 text-amber ring-amber/25',
    glow: 'bg-amber',
    pulse: 'bg-amber',
  },
  'counter-review': {
    title: 'Counter Under Review',
    description:
      'Your counter offer has been sent, it is being reviewed by the Goloot team.',
    icon: MagnifyingGlassIcon,
    chip: 'bg-cobalt/10 text-cobalt ring-cobalt/25',
    glow: 'bg-cobalt',
    pulse: 'bg-cobalt',
  },
  live: {
    title: 'Offer is Live',
    description:
      'Everything is on track! Your offer is live and ready to deliver. Grab the assets and click trackers from the Creatives.',
    icon: BroadcastIcon,
    chip: 'bg-emerald/10 text-emerald ring-emerald/25',
    glow: 'bg-emerald',
    pulse: 'bg-emerald',
  },
  paused: {
    title: 'Offer Paused',
    description: 'This offer is currently paused.',
    icon: PauseCircleIcon,
    chip: 'bg-amber/10 text-amber ring-amber/25',
    glow: 'bg-amber',
  },
  finished: {
    title: 'Offer Finished',
    description: 'This offer has ended. Thanks for your participation.',
    icon: CheckCircleIcon,
    chip: NEUTRAL_CHIP,
    glow: null,
  },
  declined: {
    title: 'Offer Declined',
    description: 'This offer was declined and is no longer active.',
    icon: XCircleIcon,
    chip: 'bg-destructive/10 text-destructive ring-destructive/25',
    glow: 'bg-destructive',
  },
};

function deriveAlertStatus(status: string): AlertStatus {
  switch (status) {
    case 'new':
    case 'reviewing':
      return 'pending';
    case 'new-counter':
      return 'action-required';
    case 'counter-in-review':
      return 'counter-review';
    case 'accepted':
    case 'active':
      return 'live';
    case 'paused':
      return 'paused';
    case 'ended':
      return 'finished';
    case 'canceled':
    case '':
      return 'declined';
    default:
      return 'pending';
  }
}

interface OfferStatusAlertProps {
  status: string;
}

export function OfferStatusAlert({ status }: OfferStatusAlertProps) {
  const alertStatus = deriveAlertStatus(status);
  const config = STATUS_CONFIG[alertStatus];
  const Icon = config.icon;

  return (
    <div className="relative w-[311px] overflow-hidden rounded-xl border border-border-hover bg-secondary p-4 shadow-premium shrink-0">
      {config.glow && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute -right-8 -top-8 size-40 rounded-full opacity-[0.3] blur-3xl',
            config.glow
          )}
        />
      )}
      <div className="relative flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              'relative flex size-9 shrink-0 items-center justify-center rounded-full ring-1',
              config.chip
            )}
          >
            {config.pulse && <RadarPing className={config.pulse} />}
            <Icon className="relative size-[18px]" weight="fill" />
          </span>
          <span className="text-base font-semibold leading-6">
            {config.title}
          </span>
        </div>
        <p className="text-xs font-medium leading-4 text-muted-foreground">
          {config.description}
        </p>
      </div>
    </div>
  );
}
